import React, {
  type ComponentRef,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Image as ExpoImage } from 'expo-image';
import { Platform, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { FadeView } from '../FadeView';
import { LoadingDots } from '../LoadingDots';
import { useImageSourceProps } from './hooks';
import { type ImageProps, type ImageWithLoadingProps } from './Image.types';
import { getImageForCurrentScreen, withHttpsProtocol } from './Image.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const Image = (props: ImageProps) => {
  const {
    source,
    'aria-label': ariaLabel,
    staticSourceUrl,
    withLoadingSkeleton = false,
    onDisplay,
    ...rest
  } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const ref = useRef<ImageRef>(null);

  // ─────────────────────────────────────────────────────────────────────

  const formattedSource = useMemo(
    () => getImageForCurrentScreen(withHttpsProtocol(source)),
    [source],
  );

  const imageSourceProps = useImageSourceProps({
    source: formattedSource,
    staticSourceUrl,
  });

  // ─────────────────────────────────────────────────────────────────────

  /**
   * Adds the `data-ot-ignore` attribute to the image element to prevent One
   * Trust from blocking the image on the web platform.
   */
  const addOneTrustIgnoreAttribute = useCallback(() => {
    if (Platform.OS !== 'web') return;

    const nativeRef = ref.current?.nativeViewRef.current;
    const castedNativeRef = nativeRef as HTMLImageElement | null | undefined;

    const isValidRef =
      castedNativeRef !== null &&
      castedNativeRef !== undefined &&
      typeof castedNativeRef === 'object' &&
      'dataset' in castedNativeRef;

    if (!isValidRef) return;

    castedNativeRef.dataset.otIgnore = 'true';
  }, []);

  const handleImageDisplay = useCallback(() => {
    onDisplay?.();
    addOneTrustIgnoreAttribute();
  }, [addOneTrustIgnoreAttribute, onDisplay]);

  // ─────────────────────────────────────────────────────────────────────

  if (withLoadingSkeleton) {
    return (
      <ImageWithLoading
        ref={ref}
        transition={theme.transitions.image}
        accessibilityLabel={ariaLabel}
        onDisplay={handleImageDisplay}
        {...imageSourceProps}
        {...rest}
      />
    );
  }

  return (
    <ExpoImage
      ref={ref}
      transition={theme.transitions.image}
      accessibilityLabel={ariaLabel}
      onDisplay={handleImageDisplay}
      {...imageSourceProps}
      {...rest}
    />
  );
};

// ─── Variations ──────────────────────────────────────────────────────────────

const ImageWithLoading = forwardRef<ImageRef, ImageWithLoadingProps>((props, ref) => {
  const { onLoadEnd, style, ...rest } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
    onLoadEnd?.();
  }, [setIsImageLoaded, onLoadEnd]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const imageStyles = [isImageLoaded ? undefined : styles.imageLoading, style];
  const loadingContainerStyles = [styles.loadingPlaceholderContainer, style];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.loadingContainer}>
      <ExpoImage
        ref={ref}
        transition={theme.transitions.image}
        onLoadEnd={handleImageLoad}
        style={imageStyles}
        {...rest}
      />

      <FadeView show={!isImageLoaded} style={loadingContainerStyles}>
        <LoadingDots color={theme.colors.CHARCOAL} />
      </FadeView>
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    width: '100%',
    height: '100%',
  },
  loadingPlaceholderContainer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.LINEN,
  },
  imageLoading: {
    opacity: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ImageRef = ComponentRef<typeof ExpoImage>;
